import type { ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'
import { useNavVisibilityStore } from '@/stores/navVisibilityStore'

interface DomainSummaryCardsProps {
  onViewChange?: (view: string) => void
  extraColumn?: ReactNode
}

interface ModuleLink {
  id: string
  label: string
}

interface DomainConfig {
  key: 'market' | 'solution' | 'business'
  label: string
  question: string
  color: string
  modules: ModuleLink[]
}

// Module mapping mirrors SidebarDomainNav so the dashboard stays in lockstep
// with the canonical domain → module structure.
const DOMAINS: DomainConfig[] = [
  {
    key: 'market',
    label: 'Market',
    question: 'Who has the problem?',
    color: 'var(--color-market)',
    modules: [
      { id: 'ai-interview', label: 'Interviews' },
      { id: 'persona-workshop', label: 'Personas' },
      { id: 'market-segmentation', label: 'Segments' },
      { id: 'competitor-map', label: 'Competitors' },
      { id: 'canvas-market', label: 'Positioning' },
    ],
  },
  {
    key: 'solution',
    label: 'Solution',
    question: 'What do we build?',
    color: 'var(--color-solution)',
    modules: [
      { id: 'value-design', label: 'Value Design' },
      { id: 'fit-thesis', label: 'Fit Thesis' },
      { id: 'canvas-solution', label: 'Problem Statement' },
      { id: 'requirements', label: 'Requirements' },
    ],
  },
  {
    key: 'business',
    label: 'Business',
    question: 'How do we make money?',
    color: 'var(--color-business)',
    modules: [
      { id: 'canvas-business', label: 'Business Model' },
      { id: 'analysis', label: 'Validation' },
    ],
  },
]

export function DomainSummaryCards({ onViewChange, extraColumn }: DomainSummaryCardsProps) {
  const { getDomainScores } = useJourneyStore()
  const { hiddenItems } = useNavVisibilityStore()
  const scores = getDomainScores()

  const visibleDomains = DOMAINS.map(d => ({
    ...d,
    modules: d.modules.filter(m => !hiddenItems.includes(m.id)),
  })).filter(d => d.modules.length > 0)

  const handleNavigate = (viewId: string) => {
    onViewChange?.(viewId)
  }

  return (
    <section aria-label="Domains" className="pt-1">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted">
          Domains
        </div>
        <div className="text-[12px] text-text-muted">
          Open a module to keep working.
        </div>
      </div>

      <div
        className={`grid grid-cols-1 border-y border-border ${
          extraColumn
            ? visibleDomains.length === 1 ? 'md:grid-cols-2' :
              visibleDomains.length === 2 ? 'md:grid-cols-3' : 'md:grid-cols-4'
            : visibleDomains.length === 1 ? 'md:grid-cols-1' :
              visibleDomains.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
        }`}
      >
        {visibleDomains.map((domain, idx) => {
          const score = scores[domain.key]
          return (
            <div
              key={domain.key}
              className={[
                'py-5 px-5',
                idx > 0 ? 'md:border-l border-border' : '',
                idx === 0 ? 'md:pl-0' : '',
                idx === DOMAINS.length - 1 ? 'md:pr-0' : '',
              ].join(' ')}
            >
              <div className="flex items-baseline gap-2.5">
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: domain.color, transform: 'translateY(-1px)' }}
                />
                <span className="font-display text-[15px] font-medium text-text leading-tight">
                  {domain.label}
                </span>
                <span
                  className="ml-auto text-text font-medium tabular-nums text-[14px] leading-tight"
                  aria-label={`${domain.label} progress: ${score} percent`}
                >
                  {score}%
                </span>
              </div>

              <div
                className="h-[2px] w-full bg-warm-200 rounded-sm mt-2.5 overflow-hidden"
                aria-hidden="true"
              >
                <div
                  className="h-full rounded-sm transition-[width] duration-500 ease-out"
                  style={{ width: `${Math.min(score, 100)}%`, backgroundColor: domain.color }}
                />
              </div>

              <p className="text-[13px] text-text-muted mt-2.5 leading-snug">
                {domain.question}
              </p>

              <ul className="mt-3.5 space-y-0">
                {domain.modules.map((module) => (
                  <li key={module.id}>
                    <button
                      type="button"
                      onClick={() => handleNavigate(module.id)}
                      className="group w-full flex items-baseline gap-3 px-2 py-1.5 -mx-2 rounded-md text-left transition-colors hover:bg-warm-100 focus:bg-warm-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1"
                    >
                      <span className="text-[13.5px] text-text leading-tight">
                        {module.label}
                      </span>
                      <ChevronRight
                        aria-hidden="true"
                        className="ml-auto w-3 h-3 text-text-muted opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 group-focus:opacity-100 group-focus:translate-x-0"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
        {extraColumn && (
          <div className="py-5 px-5 md:border-l border-border">
            {extraColumn}
          </div>
        )}
      </div>
    </section>
  )
}
